import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { ContactPerson, ContactType } from '@models/contact.model';
import { ErrorMessages, ErrorTypeMessages, ValidationService } from '@services';
import { AbstractFormContainer } from '@shared/classes/abstract-form-container';
import { EditableAutocompleteControl } from '@shared/classes/editable-autocomplete-control';
import { TypedFormControl } from '@shared/classes/typed-form-control';
import {
  AutocompleteControls,
  AutocompleteControlsTriggeredEvent,
  AutoCompleteOptionItem,
  AutocompleteOptionsFilterFn,
  AutoCompleteTransformFn,
  AutocompleteWithControlsConfig,
} from '@shared/modules/autocomplete-with-controls';
import { getContactType } from '@shared/utils';
import { ValidateContact } from '@shared/utils/contact.validator';
import { formatUAPhone } from '@shared/utils/helpers.util';
import { TypeRadioButton } from '@type-radio-group';
import { distinctUntilChanged } from 'rxjs';

@Component({
  selector: 'np-inv-contact-recipient',
  templateUrl: 'contact-recipient.component.html',
  styleUrls: ['./contact-recipient.component.scss'],
})
export class ContactRecipientComponent extends AbstractFormContainer implements EditableAutocompleteControl, TypedFormControl, OnInit {

  @Input()
  controlName = 'ContactRecipient';

  @Input()
  controlTypeName = 'ContactRecipientType';

  @Input()
  config: AutocompleteWithControlsConfig = {
    ariaLabel: 'Контакт отримувача',
    placeholder: 'Контакт',
    arrow: true,
  };

  @Input()
  controlsConfig: AutocompleteControls = null;

  @Input()
  types: Array<TypeRadioButton<ContactType>> = [
    {
      value: 'PrivatePerson',
      label: 'Фізична особа',
    },
    {
      value: 'Organization',
      label: 'Організація',
    },
  ];

  @Input()
  activeType: ContactType | null;

  @Input()
  preventInput = false;

  @Input()
  options: Array<AutoCompleteOptionItem<ContactPerson>> = [];

  @Output()
  controlChanged = new EventEmitter<ContactPerson | ''>();

  errors: ErrorTypeMessages = {
    [this.controlName]: [
      {key: 'required', message: 'This field is required'},
      {key: 'minlength', message: 'should be 3 at least'},
      {key: 'contact', message: 'Please select from autocomplete'},
    ],
  };

  errorMessages: ErrorMessages = {};
  mode: string;

  constructor(private fb: UntypedFormBuilder,
              private validationService: ValidationService) {
    super();
  }

  addFormControls() {
    this.createControl(this.form.get('RecipientSearchObject').value);
    this.form.get(this.controlName)
      .valueChanges
      .pipe(distinctUntilChanged())
      .subscribe(_ => {
        this.errorMessages =
          this.validationService.getSingleControlErrorObject(this.controlName, this.form.get(this.controlName), this.errors);
      });
  }

  createControl(defaultValue: ContactPerson | ''): void {
    this.form.setControl(this.controlName, this.fb.control(
      { value: defaultValue, disabled: defaultValue !== '' },
      [
        Validators.required,
        ValidateContact,
      ]),
    );
    this.controlChanged.emit(defaultValue);
  }

  ngOnInit() {
    super.ngOnInit();
  }

  setActiveType(value: object): void {
    this.activeType = getContactType(value);
  }

  transformFn: AutoCompleteTransformFn<ContactPerson> =
    (value: string | ContactPerson) => {
      if (value) {
        return typeof value === 'string' ?
          value : `${(value.Description || '')} ${formatUAPhone(value.Phones || '')}`;
      } else {
        return '';
      }
    };

  optionsFilterFn: AutocompleteOptionsFilterFn<ContactPerson> =
    (value: string | ContactPerson, options: Array<AutoCompleteOptionItem<ContactPerson>>) => {
      const format = v => v.replace(/[\s\+\-]+/g, '').toLowerCase();
      const group = (contact: ContactPerson) => `${contact.Description || ''} ${contact.Phones || ''}`;

      const v = format(typeof value === 'string' ? value : group(value));

      return options.filter(el => format(group(el.option)).includes(v));
    };

  cancel(): void {
    this.mode = null;
  }

  changeMode(event: AutocompleteControlsTriggeredEvent): void {
    this.mode = event.triggerName;
  }

}
